// Thin fetch wrapper for /v1/* admin API calls (sub-plan 13 §13.5).
// Responsibilities:
//   - Attach `Authorization: Bearer <jwt>` from the current Supabase session.
//   - Pass through optional `X-Captcha-Token` header.
//   - On 401: try ONE refreshSession + retry; on second 401, queryClient.clear()
//     + supabase.auth.signOut() + redirect to /login (R33 cross-account leak).
//   - Throw typed `ApiError` with message + status; callers surface via toast.

import { ENV } from "./env";
import { supabase } from "./supabase";
import { queryClient } from "./queryClient";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export interface ApiFetchInit extends Omit<RequestInit, "body"> {
  body?: unknown;
  captchaToken?: string | null;
}

async function buildHeaders(
  init: ApiFetchInit,
  accessToken?: string | null,
): Promise<HeadersInit> {
  const headers = new Headers(init.headers ?? {});
  if (init.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  if (init.captchaToken) headers.set("X-Captcha-Token", init.captchaToken);
  return headers;
}

async function doFetch(path: string, init: ApiFetchInit, token?: string | null) {
  const headers = await buildHeaders(init, token);
  const url = path.startsWith("http") ? path : `${ENV.API_BASE_URL}${path}`;
  const body =
    init.body === undefined
      ? undefined
      : typeof init.body === "string"
        ? init.body
        : JSON.stringify(init.body);
  return fetch(url, {
    method: init.method ?? (body ? "POST" : "GET"),
    headers,
    body,
    credentials: init.credentials,
    signal: init.signal,
  });
}

async function readBody(res: Response): Promise<unknown> {
  const ct = res.headers.get("Content-Type") ?? "";
  if (ct.includes("application/json")) {
    return res.json().catch(() => ({}));
  }
  return res.text().catch(() => "");
}

export async function apiFetch<T = unknown>(
  path: string,
  init: ApiFetchInit = {},
): Promise<T> {
  const { data: sessionData } = await supabase.auth.getSession();
  let token = sessionData.session?.access_token ?? null;

  let res = await doFetch(path, init, token);

  if (res.status === 401 && token) {
    // Try a one-shot refresh + retry (R21 retry-after-refresh)
    const { data: refreshed, error: refreshErr } =
      await supabase.auth.refreshSession();
    if (!refreshErr && refreshed.session?.access_token) {
      token = refreshed.session.access_token;
      res = await doFetch(path, init, token);
    }
    if (res.status === 401) {
      // Clear cache (R33), sign out, redirect.
      queryClient.clear();
      await supabase.auth.signOut();
      if (
        typeof window !== "undefined" &&
        window.location.pathname !== "/login"
      ) {
        window.location.assign("/login");
      }
      throw new ApiError("Unauthorized", 401, await readBody(res));
    }
  }

  if (!res.ok) {
    const body = (await readBody(res)) as { message?: string; error?: string };
    const message =
      (typeof body === "object" && body
        ? body.message ?? body.error
        : undefined) ?? `Request failed: ${res.status}`;
    throw new ApiError(message, res.status, body);
  }

  if (res.status === 204) return undefined as unknown as T;
  return (await readBody(res)) as T;
}
